/**
 * Oath Notification Context
 * Provides notification state and polling for /app/* routes
 */

import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getNotifications } from './oathNotificationService';
import { useOathAuth } from './oathAuthContext';

const OathNotificationContext = createContext(null);

const POLL_INTERVAL = 15000; // 15 seconds

/**
 * Hook to access notification state
 */
export const useOathNotifications = () => {
  const context = useContext(OathNotificationContext);
  if (!context) {
    throw new Error('useOathNotifications must be used within OathNotificationProvider');
  }
  return context;
};

/**
 * Notification provider with built-in polling
 */
export const OathNotificationProvider = ({ children }) => {
  const { isAuthenticated } = useOathAuth();
  const [notifications, setNotifications] = useState([]);
  const prevCountRef = useRef(0);
  const isInitialRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    const response = await getNotifications();
    if (response.success) {
      const data = response.data || [];

      // Deduplicate by notificationID
      const unique = data.filter(
        (n, i, self) => i === self.findIndex((x) => x.notificationID === n.notificationID)
      );

      // Toast for new notifications (skip initial fetch)
      if (!isInitialRef.current && unique.length > prevCountRef.current) {
        const newCount = unique.length - prevCountRef.current;
        toast.info(`You have ${newCount} new transfer bond notification${newCount !== 1 ? 's' : ''}.`);
      }

      isInitialRef.current = false;
      prevCountRef.current = unique.length;
      setNotifications(unique);
    }
  }, [isAuthenticated]);

  // Poll every 15 seconds, pause when tab is hidden
  useEffect(() => {
    if (!isAuthenticated) return;

    fetchNotifications();

    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchNotifications();
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchNotifications]);

  const value = {
    notifications,
    count: notifications.length,
    refresh: fetchNotifications,
    setNotifications,
  };

  return (
    <OathNotificationContext.Provider value={value}>
      {children}
    </OathNotificationContext.Provider>
  );
};
